import React, { useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import billingApi from '../services/billingApi';
import { getSetupIntentIdFromClientSecret } from '../utils/stripeSetupIntent';
import { getPaymentSetupReturnUrl } from '../utils/paymentReturnUrl';
import {
  getPendingSetupClientSecret,
  getPendingSetupIntentId,
} from '../utils/pendingSetupIntent';

function SetupForm({
  clientSecret,
  returnUrl,
  billingEmail,
  billingName,
  submitLabel,
  updateOnly,
  pricePerTransactionLabel,
  onSuccess,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [monthlyBillingAuthorized, setMonthlyBillingAuthorized] = useState(false);

  const finishSetup = async (setupIntentId) => {
    const secret = clientSecret || getPendingSetupClientSecret();
    const resolvedId =
      setupIntentId ||
      getSetupIntentIdFromClientSecret(secret) ||
      getPendingSetupIntentId();

    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      throw new Error('Session expired. Please sign in again.');
    }

    const result = await billingApi.completeSetup(
      accessToken,
      resolvedId || undefined,
      secret || undefined,
      {
        updateOnly,
        monthlyBillingAuthorized: updateOnly ? undefined : monthlyBillingAuthorized,
      }
    );
    onSuccess(result);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!stripe || !elements || !clientSecret) return;

    const stripeReturnUrl = returnUrl || getPaymentSetupReturnUrl();
    if (!stripeReturnUrl) {
      setError('Payment setup is misconfigured. Please contact support.');
      return;
    }

    if (!updateOnly && !monthlyBillingAuthorized) {
      setError('Please authorize monthly billing to continue.');
      return;
    }

    setSubmitting(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Please check your card details.');
        setSubmitting(false);
        return;
      }

      const confirmParams = { return_url: stripeReturnUrl };
      if (billingEmail) {
        confirmParams.payment_method_data = {
          billing_details: {
            email: billingEmail,
            name: billingName || undefined,
          },
        };
      }

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams,
      });

      if (confirmError) {
        setError(confirmError.message || 'Could not save your payment method.');
        setSubmitting(false);
        return;
      }

      let resolvedIntent = setupIntent;
      if (!resolvedIntent?.id) {
        const retrieved = await stripe.retrieveSetupIntent(clientSecret);
        resolvedIntent = retrieved?.setupIntent ?? retrieved;
      }

      const setupIntentId =
        resolvedIntent?.id || getSetupIntentIdFromClientSecret(clientSecret);

      if (!setupIntentId) {
        setError('Payment setup did not complete. Please try again.');
        setSubmitting(false);
        return;
      }

      if (resolvedIntent?.status === 'processing') {
        for (let attempt = 0; attempt < 5; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const polled = await stripe.retrieveSetupIntent(clientSecret);
          resolvedIntent = polled?.setupIntent ?? polled;
          if (resolvedIntent?.status === 'succeeded') break;
        }
      }

      if (resolvedIntent?.status && resolvedIntent.status !== 'succeeded') {
        setError(`Payment setup incomplete (${resolvedIntent.status}). Please try again.`);
        setSubmitting(false);
        return;
      }

      await finishSetup(
        resolvedIntent?.id || setupIntentId || getSetupIntentIdFromClientSecret(clientSecret)
      );
    } catch (err) {
      setError(err.message || 'Failed to save payment method.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="provider-alert provider-alert--error">{error}</div>}
      <div className="provider-stripe-wrap">
        <PaymentElement />
      </div>
      {!updateOnly && (
        <label className="provider-checkbox" style={{ marginBottom: 20 }}>
          <input
            type="checkbox"
            checked={monthlyBillingAuthorized}
            onChange={(e) => setMonthlyBillingAuthorized(e.target.checked)}
          />
          <span className="provider-checkbox-text">
            I agree to pay <strong>ReTechSys</strong> the agreed-upon fee
            {pricePerTransactionLabel ? (
              <>
                {' '}of <strong>{pricePerTransactionLabel}</strong>
              </>
            ) : null}{' '}
            per completed transaction, and I authorize ReTechSys to charge my payment method
            automatically each month for total usage during that billing period. Individual
            transactions are not charged separately.
          </span>
        </label>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting || (!updateOnly && !monthlyBillingAuthorized)}
        className="provider-btn provider-btn--primary"
      >
        {submitting ? 'Saving…' : submitLabel || 'Save payment method'}
      </button>
    </form>
  );
}

function PaymentSetupForm({
  publishableKey,
  clientSecret,
  returnUrl,
  billingEmail,
  billingName,
  submitLabel,
  updateOnly,
  pricePerTransactionLabel,
  onSuccess,
}) {
  const stripePromise = useMemo(() => {
    if (!publishableKey) return null;
    return loadStripe(publishableKey);
  }, [publishableKey]);

  const elementsOptions = useMemo(() => {
    const options = {
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#9a5c68',
          colorBackground: '#fbf8f7',
          colorText: '#1a3340',
          colorDanger: '#b42318',
          borderRadius: '10px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
    };

    if (billingEmail) {
      options.defaultValues = {
        billingDetails: {
          email: billingEmail,
          name: billingName || undefined,
        },
      };
      options.fields = {
        billingDetails: {
          email: 'never',
          name: billingName ? 'never' : 'auto',
          address: 'auto',
        },
      };
    }

    return options;
  }, [clientSecret, billingEmail, billingName]);

  if (!publishableKey || !stripePromise || !clientSecret) return null;

  return (
    <Elements key={clientSecret} stripe={stripePromise} options={elementsOptions}>
      <SetupForm
        clientSecret={clientSecret}
        returnUrl={returnUrl}
        billingEmail={billingEmail}
        billingName={billingName}
        submitLabel={submitLabel}
        updateOnly={updateOnly}
        pricePerTransactionLabel={pricePerTransactionLabel}
        onSuccess={onSuccess}
      />
    </Elements>
  );
}

export default PaymentSetupForm;
